import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { useTranslation } from 'react-i18next';
import { foldSearch } from '@eis/core/reporting';
import { displayFieldName } from './fields';

/** One join of a custom dataset (`dataset.schema.json#/properties/joins`). */
export interface JoinSpec {
  readonly left: string;
  readonly right: string;
  readonly type: 'INNER' | 'LEFT';
  readonly on: readonly {
    readonly leftField: string;
    readonly rightField: string;
    readonly cast?: 'STRING' | 'NUMBER';
  }[];
}

export interface JoinField {
  readonly name: string;
  readonly type: string;
}

export interface JoinEditorProps {
  readonly join: JoinSpec;
  /** The fields of a source alias with their catalog types. */
  readonly fieldsOf: (alias: string) => readonly JoinField[];
  readonly onChange: (join: JoinSpec) => void;
  readonly readOnly?: boolean;
}

/** S-3: the condition's two catalog types differ and no CAST is chosen. */
export function joinNeedsCast(
  join: JoinSpec,
  fieldsOf: (alias: string) => readonly JoinField[],
): boolean {
  return join.on.some((c) => {
    const l = fieldsOf(join.left).find((f) => f.name === c.leftField)?.type;
    const r = fieldsOf(join.right).find((f) => f.name === c.rightField)?.type;
    return l !== undefined && r !== undefined && l !== r && c.cast === undefined;
  });
}

/**
 * One join of the custom dataset builder (SDD-024 S-1, S-3; mockup board 8): INNER or LEFT, and
 * the field pairs. Conditions compare catalog types: a mismatch is allowed only with an explicit
 * CAST, shown as a warning token, never silent — without one the service refuses
 * `JOIN_TYPE_MISMATCH`. The editor proposes STRING.
 */
export function JoinEditor({
  join,
  fieldsOf,
  onChange,
  readOnly = false,
}: JoinEditorProps): ReactNode {
  const { t } = useTranslation('patterns');
  const left = fieldsOf(join.left);
  const right = fieldsOf(join.right);
  const typeOf = (fields: readonly JoinField[], name: string): string =>
    fields.find((f) => f.name === name)?.type ?? '';
  const setCondition = (index: number, patch: Partial<JoinSpec['on'][number]>): void => {
    onChange({
      ...join,
      on: join.on.map((c, i) => {
        if (i !== index) return c;
        const next = { ...c, ...patch };
        const lt = typeOf(left, next.leftField);
        const rt = typeOf(right, next.rightField);
        // Types differ: propose STRING; types agree: no CAST is needed.
        if (lt !== '' && rt !== '' && lt !== rt && next.cast === undefined)
          return { ...next, cast: 'STRING' as const };
        if (lt === rt && 'cast' in next) {
          const { cast: _drop, ...rest } = next;
          return rest;
        }
        return next;
      }),
    });
  };

  return (
    <Stack spacing={1} data-testid={`join-${join.left}-${join.right}`}>
      <TextField
        select
        size="small"
        label={t('reporting.join.type')}
        value={join.type}
        disabled={readOnly}
        onChange={(event) => {
          onChange({ ...join, type: event.target.value as JoinSpec['type'] });
        }}
        sx={{ maxWidth: 200 }}
      >
        <MenuItem value="LEFT">{t('reporting.join.LEFT')}</MenuItem>
        <MenuItem value="INNER">{t('reporting.join.INNER')}</MenuItem>
      </TextField>
      {join.on.map((condition, index) => {
        const lt = typeOf(left, condition.leftField);
        const rt = typeOf(right, condition.rightField);
        const mismatch = lt !== '' && rt !== '' && lt !== rt;
        return (
          <Stack
            key={`${condition.leftField}=${condition.rightField}:${String(index)}`}
            spacing={0.5}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <JoinFieldSelect
                label={t('reporting.join.left', { alias: join.left.toUpperCase() })}
                fields={left}
                value={condition.leftField}
                disabled={readOnly}
                onChange={(field) => {
                  setCondition(index, { leftField: field });
                }}
              />
              <Typography aria-hidden>=</Typography>
              <JoinFieldSelect
                label={t('reporting.join.right', { alias: join.right.toUpperCase() })}
                fields={right}
                value={condition.rightField}
                disabled={readOnly}
                onChange={(field) => {
                  setCondition(index, { rightField: field });
                }}
              />
              {!readOnly && join.on.length > 1 && (
                <IconButton
                  size="small"
                  aria-label={t('reporting.join.removeCondition')}
                  onClick={() => {
                    onChange({ ...join, on: join.on.filter((_, i) => i !== index) });
                  }}
                >
                  <DeleteOutlined fontSize="small" />
                </IconButton>
              )}
            </Stack>
            {mismatch && (
              <Alert
                severity={condition.cast === undefined ? 'error' : 'warning'}
                data-testid="join-cast-warning"
              >
                {condition.cast === undefined
                  ? t('reporting.join.castNeeded', {
                      left: `${join.left.toUpperCase()}.${condition.leftField}`,
                      leftType: lt,
                      right: `${join.right.toUpperCase()}.${condition.rightField}`,
                      rightType: rt,
                    })
                  : t('reporting.join.castWarning', {
                      left: `${join.left.toUpperCase()}.${condition.leftField}`,
                      leftType: lt,
                      right: `${join.right.toUpperCase()}.${condition.rightField}`,
                      rightType: rt,
                    })}
                {!readOnly && (
                  <TextField
                    select
                    size="small"
                    label={t('reporting.join.castTo')}
                    value={condition.cast ?? ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value === '') {
                        const { cast: _drop, ...rest } = condition;
                        onChange({ ...join, on: join.on.map((c, i) => (i === index ? rest : c)) });
                      } else {
                        onChange({
                          ...join,
                          on: join.on.map((c, i) =>
                            i === index ? { ...c, cast: value as 'STRING' | 'NUMBER' } : c,
                          ),
                        });
                      }
                    }}
                    sx={{ mt: 1, minWidth: 160 }}
                  >
                    <MenuItem value="">{t('reporting.join.noCast')}</MenuItem>
                    <MenuItem value="STRING">STRING</MenuItem>
                    <MenuItem value="NUMBER">NUMBER</MenuItem>
                  </TextField>
                )}
              </Alert>
            )}
          </Stack>
        );
      })}
      {!readOnly && (
        <Button
          size="small"
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => {
            onChange({
              ...join,
              on: [
                ...join.on,
                { leftField: left[0]?.name ?? '', rightField: right[0]?.name ?? '' },
              ],
            });
          }}
        >
          {t('reporting.join.addCondition')}
        </Button>
      )}
    </Stack>
  );
}

/**
 * One side of a join condition: a field of the view, searchable (owner feedback 2026-11-01 — a lake
 * view has dozens to hundreds of columns), matched on its name and its displayed name, accent- and
 * case-insensitively on every word.
 */
function JoinFieldSelect({
  label,
  fields,
  value,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly fields: readonly { readonly name: string }[];
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (field: string) => void;
}): ReactNode {
  const { t } = useTranslation('patterns');
  const names = fields.map((f) => f.name);
  return (
    <Autocomplete
      size="small"
      options={names}
      value={value === '' ? null : value}
      disabled={disabled}
      disableClearable={value !== ''}
      getOptionLabel={(name) => name}
      filterOptions={(list, state) => {
        const terms = foldSearch(state.inputValue.trim());
        if (terms.length === 0) return list;
        return list.filter((name) => {
          const text = foldSearch(`${name} ${displayFieldName(name)}`).join(' ');
          return terms.every((term) => text.includes(term));
        });
      }}
      noOptionsText={t('reporting.fields.noMatch')}
      onChange={(_, next) => {
        if (next !== null) onChange(next);
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={{ flex: 1 }}
    />
  );
}
